import {
    Component,
    OnInit,
    ViewChild,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { InputComponent } from "@components/input/input.component";
import { PageComponent } from "@components/page/page.component";
import { TableComponent as TableComponent_1 , TableComponent } from "@components/table/table.component";
import { GridOptions } from "ag-grid-community";
import { concatMap, firstValueFrom, from, of } from "rxjs";
import { BusyService } from "~app/common/components/busy/busy.service";
import { InputMode, PromptInput } from "~app/common/components/modal/common/prompt-dialog/prompt-dialog.component";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { AgGridTableService } from "~app/common/components/table/ag-grid-table.service";
import { RaskColumn } from "~app/common/components/table/models/rask-column";

import { ToastService } from "~app/common/components/toast/toast.service";
import { CommandService } from "~app/common/services/cqs/command.service";
import { DateUtils } from "~app/common/utils/date-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { SystemadministrationService } from "../systemadministration/systemadministration.service";
import { InmatningStatusar } from "../systemadministration/tabs/sysadm-viol3/inmatningtest/inmatningtest.models";
import { KontraktsmigreringRowVm, KontraktsmigreringStatusar, MigrateVirkeskopskontraktToViol3Command } from "./kontraktsmigrering.models";

@Component({
    selector: "rask-kontraktsmigrering",
    templateUrl: "./kontraktsmigrering.component.html",
    styleUrls: ["./kontraktsmigrering.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        PageComponent,
        InputComponent,
        FormsModule,
        ButtonComponent,
        TableComponent_1
    ]
})
export class KontraktsmigreringComponent implements OnInit {
    @ViewChild("table") tableComp: TableComponent;
    kontraktnummer: string;
    tableColumns: RaskColumn[];
    rows: KontraktsmigreringRowVm[] = [];
    token: any;
    gridOptions: GridOptions;

    private readonly administrationService = inject(SystemadministrationService);
    private readonly busyService = inject(BusyService);
    private readonly toastService = inject(ToastService);
    private readonly commandService = inject(CommandService);
    private readonly raskModalService = inject(RaskModalService);
    private readonly agGridService = inject(AgGridTableService);

    constructor() {
        this.initGrid();
    }
    async ngOnInit(): Promise<void> {
        await this.onKontraktnummerSelected();
    }

    protected async addKontraktnummer() {
        this.parseAndAdd(this.kontraktnummer, " ");
    }

    protected async copyFromClipboard() {
        try {
            const text = await navigator.clipboard.readText();
            this.parseAndAdd(text);
        } catch (err) {
            this.toastService.add("Kunde inte läsa klistrad data", 3);
            throw err;
        }
    }

    protected async parseAndAdd(text: string, split = "\r\n") {
        try {
            const splits = text.split(split).map(x => x?.trim()).filter(x => ObjectUtils.stringHasValue(x));

            const columns = splits[0].split(",").map((x, index) => ({
                header: x.trim(),
                index: index
            }));
            const rows = splits
                .map((row) => {

                    const values = row.split(",");
                    const value = values[columns.find((x) => x.header === "kontraktnummer" || x.header === "Kontraktnummer")?.index ?? 0];
                    return {
                        rowRaw: row,
                        kontraktnummer: value
                    };
                })
                .filter(s => s.kontraktnummer?.length === 6);

            if (rows.length === 0) {
                this.toastService.add("Ingen data hittades i klistrad data", 3);
                return;
            }

            let foundRows = 0;
            let modifiedRows = 0;
            let newRows = 0;
            rows.forEach((row) => {
                const vmRow = this.rows.find((x) => x.kontraktnummer === row.kontraktnummer);
                if (vmRow) {
                    if (vmRow.status !== InmatningStatusar.ny) {
                        vmRow.status = InmatningStatusar.ny;
                        modifiedRows++;
                        vmRow.setModified();
                    }
                    foundRows++;
                } else {
                    newRows++;
                    this.rows.push(KontraktsmigreringRowVm.createNew(row.kontraktnummer));
                }
            });

            this.tableComp.refreshGrid();
            this.toastService.add(`Matchade ${foundRows} rader, modifierade ${modifiedRows} rader och lade till ${newRows} nya rader`, 1);
        } catch (err) {
            this.toastService.add("Kunde inte läsa klistrad data", 3);
            throw err;
        }

    }

    protected kontraktnummerSearch = (input: string) => {
        if (input?.length >= 3) {
            return this.administrationService.query({
                $type: "FindKontraktsmigreringQuery",
                kontraktsnummer: input
            });
        } else {
            return of([]);
        }
    };

    protected canSave = () => this.rows.some((x) => x.isModified);

    protected canEnqueue = () => this.rows.some((x) => x.status === KontraktsmigreringStatusar.ny);

    protected async saveAll() {
        this.commandService.reset();

        const commands = this.rows.filter((x) => x.isModified).map((x) => new MigrateVirkeskopskontraktToViol3Command((inst) => {
            inst.kontraktnummer = x.kontraktnummer;
        }));

        const obs = from(commands).pipe(
            concatMap(c => this.administrationService.scheduleUserCommand(c, 1, "Kontraktsmigrering"))
        );

        this.busyService.addWork(obs).subscribe(async x => {
            this.kontraktnummer = null;
            await this.onKontraktnummerSelected();
            this.rows.forEach(x => x.setPristine());
        });
    }

    protected async onKontraktnummerSelected() {
        const work = this.busyService.addWork(this.administrationService.userQuery<any[]>({
            $type: "FindKontraktsmigreringQuery",
            kontraktnummer: this.kontraktnummer
        }));
        const response = await firstValueFrom(work);
        this.rows = response.map(x => KontraktsmigreringRowVm.createFromDto(
            x.kontraktnummer,
            x.status,
            DateUtils.formatDateString(x.tidpunkt, DateUtils.datetimeformatWithoutMillis),
            x.viol3,
            x.meddelande,
            x.log));
    }

    private initGrid() {
        this.gridOptions = {
            ...this.agGridService.getDefaultOptions(true),
            stopEditingWhenCellsLoseFocus: true
        };

        this.tableColumns = [
            {
                headerName: "Kontraktnummer",
                field: "kontraktnummer",
                filter: true,
                sortable: false,
                minWidth: 130,
                maxWidth: 130,
                type: "span"
            },
            {
                headerName: "Viol3",
                field: "viol3",
                filter: false,
                sortable: false,
                minWidth: 70,
                maxWidth: 70,
                type: "span"
            },
            {
                headerName: "Status",
                field: "status",
                filter: true,
                sortable: true,
                minWidth: 120,
                maxWidth: 120,
                cellClassRules: {
                    "cell-ok": (params: any) => params.value === KontraktsmigreringStatusar.ok,
                    "cell-fel": (params: any) => params.value === KontraktsmigreringStatusar.fel,
                    "cell-ny": (params: any) => params.value === KontraktsmigreringStatusar.ny
                },
                type: "span"
            },
            {
                headerName: "Tidpunkt",
                field: "tidpunkt",
                filter: false,
                sortable: true,
                minWidth: 200,
                maxWidth: 200,
                type: "span"
            },
            {
                headerName: "Meddelande",
                field: "meddelande",
                filter: true,
                sortable: false,
                minWidth: 70,
                type: "span"
            },
            {
                field: " ",
                filter: false,
                maxWidth: 110,
                headerName: "Log",
                sortable: false,
                editable: false,
                type: "button",
                text: "Log",
                onClick: (inmatning: KontraktsmigreringRowVm) => this.openLog(inmatning)
            }
        ] as RaskColumn[];
    }

    private openLog(kontraktsmigrering: KontraktsmigreringRowVm) {
        const input = new PromptInput(InputMode.readOnly, {
            bodyField: {
                value: kontraktsmigrering.log,
                label: "Log",
                type: "text"
            }
        });
        this.raskModalService.prompt("Log", input, "xl").subscribe();
    }

}
